import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CloudOff, RefreshCw } from "lucide-react";
import { flush, pendingCount, onQueueChange } from "@/lib/offline";

// Slim status strip for staff: shows offline state and queued changes,
// auto-syncs when the connection returns.
export default function OfflineSyncBadge() {
  const [online, setOnline] = useState(navigator.onLine);
  const [pending, setPending] = useState(0);
  const [syncing, setSyncing] = useState(false);

  const doFlush = async () => {
    if (!navigator.onLine || syncing) return;
    setSyncing(true);
    try {
      const res = await flush(api);
      if (res.synced > 0) toast.success(`Synced ${res.synced} offline change${res.synced !== 1 ? "s" : ""}`);
      if (res.failed > 0) toast.error(`${res.failed} offline change${res.failed !== 1 ? "s" : ""} could not be applied`);
      setPending(await pendingCount());
    } finally {
      setSyncing(false);
    }
  };

  useEffect(() => {
    pendingCount().then(setPending);
    const off = onQueueChange(setPending);
    const up = () => {
      setOnline(true);
      doFlush();
    };
    const down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    if (navigator.onLine) doFlush();
    return () => {
      off();
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (online && pending === 0) return null;
  return (
    <div
      className={`flex items-center justify-center gap-2 px-3 py-1.5 text-[11px] font-medium ${
        online ? "bg-[#0088CC] text-white" : "bg-amber-500 text-black"
      }`}
      data-testid="offline-sync-badge"
    >
      {online ? (
        <>
          <RefreshCw className={`h-3 w-3 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? `Syncing ${pending} offline change${pending !== 1 ? "s" : ""}…` : `${pending} change${pending !== 1 ? "s" : ""} waiting to sync`}
          {!syncing && (
            <button type="button" onClick={doFlush} className="underline underline-offset-2" data-testid="offline-sync-now">
              Sync now
            </button>
          )}
        </>
      ) : (
        <>
          <CloudOff className="h-3 w-3" />
          Offline — changes to vessels and quotes will be saved on this device and synced when you're back online
          {pending > 0 ? ` (${pending} queued)` : ""}
        </>
      )}
    </div>
  );
}
